// ─────────────────────────────────────────────────────────────────────────
//  useTituloActions — hook centralizado para Editar / Cancelar / Restaurar /
//  Estornar pagamento / Reverter estorno em um título (motor financeiro).
//  Fonte única para Aluno 360 (AlunoFinanceiroTab) e dashboard global
//  /v2/financeiro/titulos (DnaPayTitulos).
// ─────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin as isAdminUser } from '@/lib/auth';

const API_URL = window.location.origin;

const fmtBRL = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const TITLE_TXT = {
  edit: 'Editar título',
  cancel: 'Cancelar parcela',
  restore: 'Restaurar parcela',
  estornar: 'Estornar pagamento',
  reverter: 'Reverter estorno',
};

// Aceita shapes vindos do dashboard do aluno OU da listagem global do DnaPay
const pick = (titulo, ...keys) => {
  for (const k of keys) {
    const v = titulo?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};

export function useTituloActions({ onChanged } = {}) {
  const [dialog, setDialog] = useState(null); // { type, titulo }
  const [motivo, setMotivo] = useState('');
  const [editFields, setEditFields] = useState({});
  const [busy, setBusy] = useState(false);

  const admin = useMemo(() => {
    try { return isAdminUser(); } catch { return false; }
  }, []);

  const openAction = (type, titulo) => {
    if (type === 'edit') {
      const st = String(titulo?.status || '').toLowerCase();
      if (st === 'cancelado') {
        toast.error('Título cancelado não pode ser editado. Restaure antes.');
        return;
      }
      if ((st === 'pago' || st === 'paid') && !admin) {
        toast.error('Título pago só pode ser editado por administrador. Estorne o pagamento primeiro.');
        return;
      }
    }
    setDialog({ type, titulo });
    setMotivo('');
    setBusy(false);
    if (type === 'edit') {
      setEditFields({
        amount: pick(titulo, 'valor_original', 'amount', 'original_value', 'valor_final') ?? 0,
        due_date: String(pick(titulo, 'vencimento', 'due_date') || '').slice(0, 10),
        description: pick(titulo, 'descricao', 'description') ?? '',
        categoria_id: pick(titulo, 'categoria_id', 'categoria') ?? '',
        competencia: pick(titulo, 'competencia') ?? '',
      });
    }
  };

  const closeAction = () => { setDialog(null); setBusy(false); };

  const runAction = async () => {
    if (!dialog) return;
    if (!motivo || motivo.trim().length < 3) {
      toast.error('Motivo obrigatório (mínimo 3 caracteres)');
      return;
    }
    const { type, titulo } = dialog;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const base = `${API_URL}/api/financial/titulos/${titulo.id}`;
      let res;
      if (type === 'edit') {
        res = await fetch(base, { method: 'PUT', headers, body: JSON.stringify({ motivo, changes: editFields }) });
      } else if (type === 'cancel') {
        res = await fetch(`${base}/cancelar`, { method: 'POST', headers, body: JSON.stringify({ motivo }) });
      } else if (type === 'restore') {
        res = await fetch(`${base}/restaurar`, { method: 'POST', headers, body: JSON.stringify({ motivo }) });
      } else if (type === 'estornar') {
        res = await fetch(`${base}/estornar-pagamento`, { method: 'POST', headers, body: JSON.stringify({ motivo }) });
      } else if (type === 'reverter') {
        res = await fetch(`${base}/reverter-estorno`, { method: 'POST', headers, body: JSON.stringify({ motivo }) });
      } else {
        throw new Error(`Tipo de ação desconhecido: ${type}`);
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Erro ao executar ação');
      }
      toast.success('Ação executada e auditada');
      closeAction();
      if (typeof onChanged === 'function') onChanged();
    } catch (e) {
      toast.error(e.message || 'Erro ao executar ação');
      setBusy(false);
    }
  };

  const dialogNode = (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && closeAction()}>
      <DialogContent className="max-w-md" data-testid="action-dialog">
        {dialog && (
          <>
            <DialogHeader>
              <DialogTitle>{TITLE_TXT[dialog.type]}</DialogTitle>
              <DialogDescription>
                Título:{' '}
                <span className="font-semibold">
                  {pick(dialog.titulo, 'descricao', 'description') || '—'}
                </span>{' '}
                · {fmtBRL(pick(dialog.titulo, 'valor_original', 'amount', 'original_value', 'valor_final'))}
                {dialog.type === 'edit' && String(dialog.titulo.status || '').toLowerCase() === 'pago' && (
                  <span className="block mt-1 text-amber-700 text-xs">
                    ⚠️ Título já pago — alteração será auditada.
                  </span>
                )}
                {dialog.type === 'edit' && (
                  dialog.titulo.boleto || dialog.titulo.pix ||
                  dialog.titulo.bank_slip_code || dialog.titulo.pix_code ||
                  dialog.titulo.nota_fiscal
                ) && (
                  <span className="block mt-1 text-amber-700 text-xs">
                    ⚠️ Há boleto/PIX/NF vinculados. Revise manualmente após salvar.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {dialog.type === 'edit' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs">Descrição</Label>
                    <Input
                      value={editFields.description || ''}
                      onChange={e => setEditFields(f => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Valor</Label>
                    <Input
                      type="number" step="0.01"
                      value={editFields.amount ?? ''}
                      onChange={e => setEditFields(f => ({ ...f, amount: Number(e.target.value) }))}
                      data-testid="edit-amount"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Vencimento</Label>
                    <Input
                      type="date"
                      value={(editFields.due_date || '').slice(0, 10)}
                      onChange={e => setEditFields(f => ({ ...f, due_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Categoria</Label>
                    <Input
                      value={editFields.categoria_id || ''}
                      onChange={e => setEditFields(f => ({ ...f, categoria_id: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Competência (YYYY-MM)</Label>
                    <Input
                      value={editFields.competencia || ''}
                      onChange={e => setEditFields(f => ({ ...f, competencia: e.target.value }))}
                    />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">Motivo *</Label>
                <Input
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                  placeholder="Mínimo 3 caracteres"
                  data-testid="action-motivo"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeAction} disabled={busy}>Cancelar</Button>
              <Button
                onClick={runAction}
                disabled={busy}
                data-testid="action-confirm"
                className={
                  dialog.type === 'cancel' || dialog.type === 'estornar'
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : dialog.type === 'restore' || dialog.type === 'reverter'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-violet-600 hover:bg-violet-700'
                }
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  return { openAction, closeAction, isAdmin: admin, dialogNode };
}

export default useTituloActions;
